import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, CalendarDays, Clock, X, RefreshCw, CheckCircle2, Sparkles, Loader2,
} from "lucide-react";

const STATUS = {
  confirmado: { label: "Confirmado", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", cls: "border-red-500/30 bg-red-500/10 text-red-300", icon: X },
  remarcado: { label: "Remarcado", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300", icon: RefreshCw },
  pendente: { label: "Pendente", cls: "border-slate-500/30 bg-slate-500/10 text-slate-300", icon: Clock },
};
const FILTERS = ["todos", "confirmado", "cancelado", "remarcado"];

export default function Agenda() {
  const [appts, setAppts] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(new URLSearchParams(window.location.search).get("novo") === "1");
  const [services, setServices] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [blockForm, setBlockForm] = useState(null);
  const loadExtras = () => { api.get("/blocks").then(({ data }) => setBlocks(data)).catch(() => {}); api.get("/waitlist").then(({ data }) => setWaitlist(data)).catch(() => {}); };
  useEffect(() => { api.get("/company").then(({ data }) => setServices((data.services || []).filter((s) => s.active !== false))).catch(() => {}); loadExtras(); }, []);
  const saveBlock = async () => {
    if (!blockForm?.start || !blockForm?.end) { toast.error("Informe início e fim."); return; }
    try { await api.post("/blocks", blockForm); toast.success("Horário bloqueado."); setBlockForm(null); loadExtras(); } catch (e) { toast.error(e.response?.data?.detail || "Erro ao bloquear."); }
  };
  const [reschedule, setReschedule] = useState(null);
  const [form, setForm] = useState({ client_id: "", title: "", scheduled_at: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);

  useEffect(() => { if (open) api.get("/appointments/slots", { params: { service: form.title || "" } }).then(({ data }) => setSlots(data.slots.slice(0, 8))).catch(() => {}); }, [open, form.title]); // eslint-disable-line
  const slotLabel = (s) => new Date(s).toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  const load = async () => {
    const params = filter !== "todos" ? { status: filter } : {};
    const { data } = await api.get("/appointments", { params });
    setAppts(data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const create = async () => {
    if (!form.client_id || !form.title || !form.scheduled_at) { toast.error("Preencha cliente, título e data."); return; }
    setSaving(true);
    try {
      await api.post("/appointments", form);
      toast.success("Agendamento criado! Confirmação enviada ao cliente via WhatsApp.");
      setOpen(false); setForm({ client_id: "", title: "", scheduled_at: "", notes: "" });
      load();
    } catch { toast.error("Erro ao criar agendamento."); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    await api.post(`/appointments/${id}/cancel`);
    toast.success("Agendamento cancelado. Cliente notificado.");
    load();
  };
  const doReschedule = async () => {
    if (!reschedule?.scheduled_at) return;
    await api.post(`/appointments/${reschedule.id}/reschedule`, { scheduled_at: reschedule.scheduled_at });
    toast.success("Agendamento remarcado. Cliente notificado.");
    setReschedule(null); load();
  };

  const fmt = (s) => {
    const d = new Date(s);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };
  const daysWithAppts = appts.map((a) => { try { return new Date(a.scheduled_at); } catch { return null; } }).filter(Boolean);

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="agenda-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 inline-flex items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400"><Sparkles className="h-3.5 w-3.5" /> Agendamentos</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Agenda</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="agenda-new-btn" className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300"><Plus className="mr-1.5 h-4 w-4" /> Novo agendamento</Button>
          </DialogTrigger>
          <DialogContent className="border-slate-800 bg-slate-950 text-slate-100">
            <DialogHeader><DialogTitle className="font-display">Novo agendamento</DialogTitle><DialogDescription className="text-slate-400">O cliente recebe a confirmação no WhatsApp automaticamente.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Cliente</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger className="border-slate-700 bg-slate-900/60" data-testid="agenda-client-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Título</Label>
                {services.length > 0 ? (
                  <Select value={form.title} onValueChange={(v) => setForm((f) => ({ ...f, title: v }))}>
                    <SelectTrigger className="border-slate-700 bg-slate-900/60" data-testid="agenda-service-select"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                    <SelectContent className="border-slate-800 bg-slate-950 text-slate-100">{services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name} · {s.duration} min{s.price ? ` · R$ ${Number(s.price).toFixed(0)}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input data-testid="agenda-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Consulta, avaliação..." className="border-slate-700 bg-slate-900/60" />}
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Data e hora</Label>
                <Input type="datetime-local" data-testid="agenda-datetime-input" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} className="border-slate-700 bg-slate-900/60 [color-scheme:dark]" />
                {slots.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid="agenda-free-slots">
                    <span className="w-full text-[11px] uppercase tracking-wider text-slate-500">Próximos horários livres</span>
                    {slots.map((s) => (
                      <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, scheduled_at: s }))} data-testid="agenda-slot-chip"
                        className={`rounded-full border px-2.5 py-1 text-xs transition-all ${form.scheduled_at === s ? "border-cyan-400 bg-cyan-400/15 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>{slotLabel(s)}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Observações</Label>
                <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="border-slate-700 bg-slate-900/60" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="agenda-save-btn" className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar agendamento"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
        <div className="glow-card rounded-2xl p-4" data-testid="agenda-calendar-view">
          <Calendar mode="single" modifiers={{ hasAppt: daysWithAppts }}
            modifiersClassNames={{ hasAppt: "bg-cyan-400/20 text-cyan-200 rounded-md" }}
            className="text-slate-100" />
          <p className="mt-2 text-center text-xs text-slate-500">Dias em ciano possuem agendamentos</p>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={f === "todos" ? "agenda-filter-todos" : `agenda-status-filter-${f}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium capitalize transition-all ${filter === f ? "border-cyan-400 bg-cyan-400/15 text-cyan-300" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:text-slate-200"}`}>
                {f === "todos" ? "Todos" : STATUS[f].label}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {appts.length === 0 && (
              <div className="glow-card rounded-2xl">
                <EmptyState icon={CalendarDays} testid="agenda-empty"
                  title={filter !== "todos" ? `Nenhum agendamento ${STATUS[filter]?.label.toLowerCase()}` : "Agenda livre"}
                  desc={filter !== "todos" ? "Troque o filtro para ver outros status." : "Crie um agendamento e o cliente recebe a confirmação no WhatsApp automaticamente, com lembrete 1h antes."}
                  actionLabel={filter !== "todos" ? undefined : "Criar primeiro agendamento"} onAction={() => setOpen(true)} />
              </div>
            )}
            {appts.map((a) => {
              const st = STATUS[a.status] || STATUS.pendente;
              return (
                <div key={a.id} className="glow-card flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4" data-testid="agenda-appointment-card">
                  <div className="flex items-center gap-4">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-slate-800/70 text-cyan-400"><CalendarDays className="h-5 w-5" /></div>
                    <div>
                      <p className="font-semibold text-slate-100">{a.title}</p>
                      <p className="text-sm text-slate-400">{a.client_name}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" /> {fmt(a.scheduled_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${st.cls}`}><st.icon className="h-3 w-3" /> {st.label}</span>
                    {a.status !== "cancelado" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setReschedule({ id: a.id, scheduled_at: "" })} className="border-slate-700 bg-slate-900/50 text-slate-300 hover:text-amber-300" data-testid="agenda-reschedule-btn"><RefreshCw className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => cancel(a.id)} className="border-slate-700 bg-slate-900/50 text-slate-300 hover:text-red-300" data-testid="agenda-cancel-btn"><X className="h-4 w-4" /></Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={!!reschedule} onOpenChange={(v) => !v && setReschedule(null)}>
        <DialogContent className="border-slate-800 bg-slate-950 text-slate-100">
          <DialogHeader><DialogTitle className="font-display">Remarcar agendamento</DialogTitle><DialogDescription className="text-slate-400">O cliente será avisado da nova data pelo WhatsApp.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Nova data e hora</Label>
            <Input type="datetime-local" value={reschedule?.scheduled_at || ""} onChange={(e) => setReschedule((r) => ({ ...r, scheduled_at: e.target.value }))} className="border-slate-700 bg-slate-900/60 [color-scheme:dark]" data-testid="agenda-reschedule-input" />
          </div>
          <DialogFooter><Button onClick={doReschedule} className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300" data-testid="agenda-reschedule-confirm">Remarcar e notificar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="glow-card rounded-2xl p-5" data-testid="agenda-blocks">
          <div className="flex items-center justify-between"><p className="font-display font-semibold text-white">Bloqueios (feriados, ausências)</p><Button size="sm" variant="outline" onClick={() => setBlockForm({ start: "", end: "", reason: "" })} className="border-slate-700 bg-slate-900/50 text-cyan-300" data-testid="agenda-block-open">+ Bloquear horário</Button></div>
          {blockForm && (
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <Input type="datetime-local" value={blockForm.start} onChange={(e) => setBlockForm({ ...blockForm, start: e.target.value })} className="border-slate-700 bg-slate-900/60 [color-scheme:dark]" data-testid="agenda-block-start" />
              <Input type="datetime-local" value={blockForm.end} onChange={(e) => setBlockForm({ ...blockForm, end: e.target.value })} className="border-slate-700 bg-slate-900/60 [color-scheme:dark]" data-testid="agenda-block-end" />
              <Input placeholder="Motivo" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} className="border-slate-700 bg-slate-900/60" data-testid="agenda-block-reason" />
              <Button onClick={saveBlock} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300" data-testid="agenda-block-save">Salvar</Button>
            </div>
          )}
          <div className="mt-3 space-y-1.5">
            {blocks.length === 0 && <p className="text-sm text-slate-500">Nenhum bloqueio futuro.</p>}
            {blocks.map((b) => <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm" data-testid="agenda-block-row"><span className="text-slate-200">{b.start.replace("T", " ")} → {b.end.replace("T", " ")} <span className="text-slate-500">{b.reason}</span></span><button onClick={() => api.delete(`/blocks/${b.id}`).then(loadExtras)} className="text-xs text-slate-500 hover:text-red-300" data-testid="agenda-block-delete">remover</button></div>)}
          </div>
        </div>
        <div className="glow-card rounded-2xl p-5" data-testid="agenda-waitlist">
          <p className="font-display font-semibold text-white">Lista de espera</p>
          <div className="mt-3 space-y-1.5">
            {waitlist.length === 0 && <p className="text-sm text-slate-500">Ninguém aguardando. A IA oferece a lista quando não há horário.</p>}
            {waitlist.map((w) => <div key={w.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm" data-testid="agenda-waitlist-row"><span className="text-slate-200">{w.client_name} <span className="text-slate-500">· {w.service || "qualquer serviço"} · {w.status === "avisado" ? "avisado do horário livre" : "aguardando"}</span></span><button onClick={() => api.delete(`/waitlist/${w.id}`).then(loadExtras)} className="text-xs text-slate-500 hover:text-red-300" data-testid="agenda-waitlist-remove">remover</button></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
